<script>
  import { play, playSequence, loadAudioManifest, unlockAudio, hasOverride } from './audio.js'
  import { buildCustomWord } from './content.js'

  // A consonant × vowel blending table (like a Kannada ಗುಣಿತ chart). Tap a vowel or
  // consonant header to hear it alone, a cell to hear the CV blend, or ▶ to chant a
  // row. Sounds come from the app's phoneme library (blends stitched from phonemes,
  // or a recorded blend clip bl:<c>_<v> when one exists).
  let { onexit } = $props()

  // [phoneme code, letter, example]
  const CONS = [
    ['b', 'b', 'bat'], ['k', 'c', 'cat'], ['d', 'd', 'dog'], ['f', 'f', 'fan'],
    ['g', 'g', 'go'], ['h', 'h', 'hat'], ['j', 'j', 'jam'], ['l', 'l', 'leg'],
    ['m', 'm', 'man'], ['n', 'n', 'no'], ['p', 'p', 'pig'],
    ['r', 'r', 'red'], ['s', 's', 'sun'], ['t', 't', 'top'], ['v', 'v', 'van'],
    ['w', 'w', 'wet'], ['y', 'y', 'yes'], ['z', 'z', 'zoo'], ['ch', 'ch', 'chip'],
    ['sh', 'sh', 'ship'], ['th', 'th', 'thin'],
  ]
  const VOW = [
    ['a', 'a', 'cat'], ['e', 'e', 'bed'], ['i', 'i', 'sit'], ['o', 'o', 'pot'], ['u', 'u', 'cup'],
    ['ae', 'ay', 'day'], ['ee', 'ee', 'see'], ['ie', 'y', 'my'], ['oe', 'oe', 'toe'], ['uu', 'oo', 'moo'],
    ['ow', 'ow', 'cow'], ['oi', 'oy', 'boy'], ['ar', 'ar', 'car'], ['or', 'or', 'for'], ['ur', 'er', 'her'],
  ]

  let playing = $state('') // 'ci-vi' | 'v:vi' | 'c:ci'
  let loaded = false
  let chantToken = 0

  async function ensure() {
    if (loaded) return
    unlockAudio()
    const sounds = [...CONS, ...VOW].map(([code, letter]) => ({ text: letter, code }))
    const { manifest } = buildCustomWord('cv-phonemes', sounds)
    await loadAudioManifest(manifest)
    loaded = true
  }

  function playBlend(cCode, vCode) {
    const bk = 'bl:' + cCode + '_' + vCode
    if (hasOverride(bk)) play(bk)                              // recorded blend clip wins
    else playSequence(['tph:' + cCode, 'tph:' + vCode])       // else stitch the two phonemes
  }
  const clearLater = (tag, ms) => setTimeout(() => { if (playing === tag) playing = '' }, ms)

  async function cell(ci, vi) {
    chantToken++
    await ensure()
    const tag = ci + '-' + vi
    playing = tag
    playBlend(CONS[ci][0], VOW[vi][0])
    clearLater(tag, 900)
  }
  async function vowel(vi) { chantToken++; await ensure(); const t = 'v:' + vi; playing = t; play('ph:' + VOW[vi][0]); clearLater(t, 700) }
  async function cons(ci) { chantToken++; await ensure(); const t = 'c:' + ci; playing = t; play('ph:' + CONS[ci][0]); clearLater(t, 700) }
  async function chant(ci) {
    await ensure()
    const my = ++chantToken
    for (let vi = 0; vi < VOW.length; vi++) {
      if (my !== chantToken) return
      playing = ci + '-' + vi
      playBlend(CONS[ci][0], VOW[vi][0])
      await new Promise((r) => setTimeout(r, 750))
    }
    if (my === chantToken) playing = ''
  }
  function stop() { chantToken++; playing = '' }
</script>

<div class="cv">
  <div class="toolbar">
    <button class="tb" onclick={onexit} aria-label="Back to menu">‹ Menu</button>
    <button class="tb" onclick={stop}>■ Stop</button>
  </div>
  <div class="head">
    <h2>🔡 CV blending</h2>
    <p>Tap a sound to hear it · a cell for the blend · ▶ to chant a row</p>
  </div>

  <div class="scroll">
    <table>
      <thead>
        <tr>
          <th class="corner"></th>
          {#each VOW as v, vi}
            <th><button class="vhdr" class:on={playing === 'v:' + vi} onclick={() => vowel(vi)} title={v[2]}>{v[1]}</button></th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each CONS as c, ci}
          <tr>
            <th class="rowh">
              <button class="chdr" class:on={playing === 'c:' + ci} onclick={() => cons(ci)} title={c[2]}>{c[1]}</button>
              <button class="chant" onclick={() => chant(ci)} aria-label={`chant ${c[1]} row`}>▶</button>
            </th>
            {#each VOW as v, vi}
              <td class="cell" class:on={playing === ci + '-' + vi} onclick={() => cell(ci, vi)}>{c[1]}{v[1]}</td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
</div>

<style>
  .cv { position: fixed; inset: 0; display: flex; flex-direction: column; align-items: center; gap: 1.2vh; padding: 7vh 1.5vh 2vh; box-sizing: border-box; }
  .toolbar { position: fixed; top: 1.2vh; left: 1.4vh; z-index: 25; display: flex; gap: 1vh; }
  .tb { border: none; background: rgba(255,255,255,0.9); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 1.7vh; padding: 0.8vh 1.5vh; border-radius: 999px; box-shadow: 0 3px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .tb:active { transform: translateY(2px); box-shadow: 0 1px 0 rgba(0,0,0,0.15); }
  .head { text-align: center; color: #fffef0; flex: none; }
  .head h2 { margin: 0; font-size: 3vh; text-shadow: 0 2px 0 rgba(0,0,0,0.15); }
  .head p { margin: 0.3vh 0 0; font-weight: 700; opacity: 0.95; font-size: 1.6vh; }

  .scroll { flex: 1; min-height: 0; width: 100%; overflow: auto; display: flex; justify-content: center; }
  table { border-collapse: separate; border-spacing: 0.5vh; }
  th, td { text-align: center; }
  thead th { position: sticky; top: 0; z-index: 2; background: transparent; padding-bottom: 0.3vh; }
  tbody th.rowh { position: sticky; left: 0; z-index: 1; }

  .vhdr { border: none; background: #6b4a8a; color: #fff; font-family: inherit; font-weight: 800; font-size: 2vh; width: 8vh; height: 5vh; border-radius: 10px; cursor: pointer; }
  .rowh { display: flex; gap: 0.4vh; align-items: center; }
  .chdr { border: none; background: #2d6a7a; color: #fff; font-family: inherit; font-weight: 800; font-size: 2.1vh; width: 7vh; height: 6.4vh; border-radius: 10px; cursor: pointer; }
  .chant { border: none; background: #2d6a7a; color: #fff; opacity: 0.8; font-size: 1.6vh; width: 3vh; height: 6.4vh; border-radius: 10px; cursor: pointer; }
  .chant:active, .vhdr:active, .chdr:active { transform: translateY(2px); }
  .cell {
    background: #fffef0; color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 2.1vh;
    width: 8vh; height: 6.4vh; border-radius: 10px; cursor: pointer; user-select: none;
    box-shadow: 0 2px 0 rgba(0,0,0,0.12); border: 2px solid transparent; transition: background 0.1s;
  }
  .cell:hover { border-color: #2d6a7a; }
  .cell.on, .vhdr.on, .chdr.on { background: #ff7043; color: #fff; }
</style>
